import { WidgetTagOptionType } from '../../types/widget-tag-option-type';
import { stripHtml } from '../../utils/strip-html';
import { ObjectTypes } from '../../types/object-type';
import { BehaviorSubject, Subject } from 'rxjs';
import { Directive, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { WidgetPlaceHolderControlModel } from '@fi-shared/widget/widget-place-holder-control.model';
import { ObjectTagFilterParameter, SelectedSort } from '@fi-shared/models';
import { SideListOptionType, SortDirection, SortType } from '@fi-shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { WidgetSharedService } from '@fi-shared/widget/widget-shared.service';

export interface IDocumentListingItem {
    ObjectId: number;
    ObjectType;
    Name: string;
    Summary: string;
    ChangedOn;
    IconClass: string;
}

@Directive()
export abstract class WidgetViewContentDocumentListingBase implements OnInit, OnDestroy {
    @Input() headerText: string;
    @Input() control: WidgetPlaceHolderControlModel;

    Data: IDocumentListingItem[] = [];
    loading$ = new BehaviorSubject<boolean>(false);

    ngOnInit() {
        this.loadData();
    }

    protected mediaBankHelperService: MediaBankHelperService;
    protected appTextService: AppTextService;
    protected widgetSharedService: WidgetSharedService;

    constructor(private injector: Injector) {
        this.mediaBankHelperService = injector.get(MediaBankHelperService);
        this.appTextService = injector.get(AppTextService);
        this.widgetSharedService = injector.get(WidgetSharedService);
    }

    private _isEmpty$ = new BehaviorSubject<boolean>(true);
    get isEmpty$() {
        return this._isEmpty$.asObservable();
    }

    lastLoaded = false;
    loadData() {
        const promise = new Promise<void>((resolve, reject) => {
            if (this.loading$.value || this.lastLoaded) {
                resolve();
            }

            this.loading$.next(true);
            this.loadItems()
                .then((data) => {
                    this.Data = this.Data.concat(data.map(this.createDataItem));
                    this.lastLoaded = !data.length;
                    resolve();
                })
                .catch(() => {
                    this.Data = [];
                    reject();
                })
                .finally(() => {
                    this.loading$.next(false);
                    //this.loadMoreDone.next(this.Data.length);
                });
        });
        promise.finally(() => {
            this._isEmpty$.next(this.Data.length === 0);
        });

        return promise;
    }

    loadItems(): Promise<any> {
        if (
            ((this.control.Settings.WidgetTagOptionType == WidgetTagOptionType.AnyRelated ||
                this.control.Settings.WidgetTagOptionType == WidgetTagOptionType.AllRelated) &&
                !this.control.objectId) ||
            (!this.control.Settings.Pages && !this.control.Settings.Files)
        ) {
            return Promise.resolve([]);
        }

        // load items if tags selected
        if (
            !this.control.Settings.UseWidgetTagOptionType ||
            (this.control.TagIds && this.control.TagIds.length > 0) ||
            this.control.Settings.WidgetTagOptionType == WidgetTagOptionType.All ||
            this.control.Settings.WidgetTagOptionType == WidgetTagOptionType.AnyRelated ||
            this.control.Settings.WidgetTagOptionType == WidgetTagOptionType.AllRelated
        ) {
            return this.widgetSharedService.getItemsByTagFilter(this.getObjectTagFilterParameter());
        }
        // clear items if tags is not selected
        else {
            return Promise.resolve([]);
        }
    }

    createDataItem(itemData: IDocumentListingItem): IDocumentListingItem {
        return {
            ObjectId: itemData.ObjectId,
            ObjectType: itemData.ObjectType,
            IconClass: itemData.ObjectType == ObjectTypes.Document ? 'fa fa-file-o' : 'fa fa-image',
            Name: itemData.Name,
            Summary: itemData.Summary && itemData.Summary.length > 0 ? stripHtml(itemData.Summary) : '',
            ChangedOn: itemData.ChangedOn,
        };
    }

    isItemTypeMediaBank(item: IDocumentListingItem): boolean {
        return item.ObjectType === ObjectTypes.MediaBank;
    }

    onFileClick(item: IDocumentListingItem) {
        this.mediaBankHelperService.showOrDownloadFile(item.ObjectId);
    }

    itemClick($event: Event, item: IDocumentListingItem) {}

    getItemHref(item) {}

    getSelectedSort(): SelectedSort {
        let selectedSortItem = null;
        switch (this.control.Settings.SortingType) {
            case SortType.Alphabetical:
                selectedSortItem = {
                    ColumnName: 'Name',
                    SortDirection: SortDirection.Ascending,
                };
                break;
            case SortType.ReversedAlphabetical:
                selectedSortItem = {
                    ColumnName: 'Name',
                    SortDirection: SortDirection.Descending,
                };
                break;
            case SortType.Chronological:
                selectedSortItem = {
                    ColumnName: 'ChangedOn',
                    SortDirection: SortDirection.Ascending,
                };
                break;
            case SortType.ReversedChronological:
                selectedSortItem = {
                    ColumnName: 'ChangedOn',
                    SortDirection: SortDirection.Descending,
                };
                break;
        }
        return new SelectedSort(selectedSortItem);
    }

    getRecordsToTake() {
        return parseInt(this.control.Settings.NumberOfRecordsToTake) || 20;
    }

    isEmpty() {
        return this.Data.length === 0;
    }

    GetEmptyContentText() {
        if (
            (this.control.Settings.WidgetTagOptionType == WidgetTagOptionType.AnyRelated ||
                this.control.Settings.WidgetTagOptionType == WidgetTagOptionType.AllRelated) &&
            !this.control.objectId
        ) {
            return this.appTextService.get('widget.items_any_tags_text');
        }

        return this.getHeaderTitle();
    }

    getObjectTagFilterParameter(): ObjectTagFilterParameter {
        const item = new ObjectTagFilterParameter();

        item.Initialize(
            this.control.TagIds,
            this.Data.length,
            this.getRecordsToTake(),
            this.control.Settings.ShowOnlyNewsFeedItems,
            this.getSelectedSort(),
            this.control.Settings.DisplayIcon,
            this.control.Settings.WidgetTagOptionType,
            this.control.Settings.UseWidgetTagOptionType,
        );
        item.ObjectId = parseInt(this.control.objectId);
        item.ObjectType = parseInt(this.control.objectType);
        item.LoadDocuments = this.control.Settings.Pages;
        item.LoadMediaBankFiles = this.control.Settings.Files;

        if (this.control.Settings.ShowOnlyUnread) {
            item.FilterType = SideListOptionType.OnlyNotSeen;
        }

        return item;
    }

    abstract getHeaderTitle(): string;

    protected ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
